/**
 * Candidate Apply API Service
 * Handles job application (CV submission) functionality
 */

import http from '../client';
import { getTrackedSource } from '../../utils/sourceDetection';
import { isEmployerAccount, EMPLOYER_BLOCKED_MESSAGE } from '../../context/RoleContext';

// Standardized response handlers
const handleApiSuccess = (response) => ({
  success: true,
  data: response.data?.data || response.data,
  status: response.status,
  message: response.data?.message || 'Success'
});

const handleApiError = (error) => ({
  success: false,
  error: error.message || 'An error occurred',
  status: error.status || 500,
  requiresAuth: error.status === 401
});

/**
 * Apply to a job assessment (Send CV)
 * Creates an AssessmentPlayer record with isApplied=true
 * @param {number} assessmentId - Job assessment ID to apply to
 * @param {string} [selectedDays] - Comma-separated days for part-time jobs (e.g., "MON,WED")
 * @returns {Promise<Object>} { success, data: { assessmentPlayerId, message } }
 */
export const applyToJob = async (assessmentId, selectedDays) => {
  // Applying belongs to the jobseeker role. Checked here rather than only at each
  // button because the apply action is triggered from the feed, the map, saved jobs,
  // the preview drawer and the job page — this is the one path all of them share.
  if (isEmployerAccount()) {
    return { success: false, error: EMPLOYER_BLOCKED_MESSAGE };
  }

  try {
    const tracked = getTrackedSource();
    const params = { assessmentId };
    if (tracked?.source) {
      params.sourceChannel = tracked.source;
    }
    if (selectedDays) {
      params.selectedDays = selectedDays;
    }
    const response = await http.post('/candidate/apply', null, { params });
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Check if current user has applied to a job assessment
 * @param {number} assessmentId - Job assessment ID to check
 * @returns {Promise<Object>} { success, data: { hasApplied, assessmentId, selectedDays } }
 */
export const hasApplied = async (assessmentId) => {
  try {
    const response = await http.get('/candidate/has-applied', { assessmentId });
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Update selected working days for an existing part-time job application
 * @param {number} assessmentId - Job assessment ID
 * @param {string} selectedDays - Comma-separated days (e.g., "MON,WED")
 * @returns {Promise<Object>} { success, data: { selectedDays } }
 */
export const updateSelectedDays = async (assessmentId, selectedDays) => {
  try {
    const response = await http.put('/candidate/apply/days', null, {
      params: { assessmentId, selectedDays }
    });
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get count of jobs current user has applied to
 * @returns {Promise<Object>} { success, data: { count } }
 */
export const getAppliedCount = async () => {
  try {
    const response = await http.get('/candidate/applied-count');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get list of jobs current user has applied to
 * @returns {Promise<Object>} { success, data: Array }
 */
export const getAppliedList = async () => {
  try {
    const response = await http.get('/candidate/applied-list');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get count of jobs current user has interviewed for (chatted)
 * @returns {Promise<Object>} { success, data: { count } }
 */
export const getInterviewedCount = async () => {
  try {
    const response = await http.get('/candidate/interviewed-count');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get list of jobs current user has interviewed for
 * @returns {Promise<Object>} { success, data: Array }
 */
export const getInterviewedList = async () => {
  try {
    const response = await http.get('/candidate/interviewed-list');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get combined job status (applied + liked job IDs) in single request
 * Used on Home page to reduce API calls
 * @returns {Promise<Object>} { success, applied: number[], liked: number[] }
 */
export const getJobStatus = async () => {
  try {
    const response = await http.get('/candidate/job-status');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get count of tests assigned to current user
 * @returns {Promise<Object>} { success, data: { count } }
 */
export const getTestCount = async () => {
  try {
    const response = await http.get('/candidate/test-count');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Submit date requests for a part-time assessment (PENDING booking requests)
 * @param {number} assessmentId
 * @param {string[]} workDates - ISO date strings (e.g., ["2026-04-14", "2026-04-21"])
 * @returns {Promise<Object>}
 */
export const requestDates = async (assessmentId, workDates) => {
  try {
    const response = await http.post(
      '/part-time/bookings/batch',
      { assessmentId, workDates },
      { headers: { 'X-Client-Source': 'DIIL' } },
    );
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

export const PART_TIME_WINDOW_DAYS = 20;

/** Today as YYYY-MM-DD in Ulaanbaatar — the server's day boundary, not the browser's. */
export const ubToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Ulaanbaatar' }).format(new Date());

const addDays = (isoDate, days) => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Bookable part-time shifts across every company, one row per (assessment, date) with
 * availableSlots > 0. Past, full and non-working days are already left out by the server.
 *
 * Public, and deliberately asked with the part-time app's exact window (Ulaanbaatar today
 * + 19 days, no branch): the server caches this cross-company aggregate by
 * (startDate, endDate, branchId) in 32 entries, so a per-job window from every job page
 * would each be a cold recompute of the query that already ran hot at 12–17s. Callers
 * filter by assessmentId.
 * @returns {Promise<Object>} { success, data: AvailableDate[] }
 */
export const getPartTimeAvailable = async () => {
  const startDate = ubToday();
  const endDate = addDays(startDate, PART_TIME_WINDOW_DAYS - 1);
  try {
    const response = await http.get('/part-time/available', { startDate, endDate }, { skipAuth: true });
    return { success: true, data: Array.isArray(response.data) ? response.data : [] };
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * The signed-in person's active part-time bookings (PENDING / APPROVED / checked in),
 * across every job. Jobseeker only — an employer token has no candidate behind it.
 * @returns {Promise<Object>} { success, data: WorkerBooking[] }
 */
export const getMyPartTimeBookings = async () => {
  try {
    const response = await http.get('/part-time/bookings/my');
    return { success: true, data: Array.isArray(response.data) ? response.data : [] };
  } catch (error) {
    return handleApiError(error);
  }
};

const candidateApplyService = {
  applyToJob,
  hasApplied,
  updateSelectedDays,
  getAppliedCount,
  getAppliedList,
  getInterviewedCount,
  getInterviewedList,
  getJobStatus,
  getTestCount,
  requestDates
};

export default candidateApplyService;
